import type { IconType } from 'react-icons'
import {
  TbArrowsExchange,
  TbBolt,
  TbBuilding,
  TbChartBar,
  TbChartPie,
  TbCloudUpload,
  TbDatabase,
  TbFileImport,
  TbFolder,
  TbHome,
  TbMail,
  TbRefresh,
  TbReportAnalytics,
  TbScale,
  TbSearch,
  TbShield,
  TbShieldCheck,
  TbUserSearch,
} from 'react-icons/tb'
import type { LoginMenu } from '@features/auth'
import { normalizeLegacyMenuPath } from './menuPathResolver'

type IconRule = {
  test: (text: string) => boolean
  icon: IconType
}

/** Parent menu labels → Tabler icons (ti-* equivalents via react-icons/tb). */
const LABEL_ICON_MAP: Record<string, IconType> = {
  home: TbHome,
  welcome: TbHome,
  'update inward data': TbCloudUpload,
  'master updation': TbDatabase,
  queries: TbSearch,
  company: TbBuilding,
  'corporate action': TbBolt,
  'outstanding shares': TbChartPie,
  reconciliation: TbRefresh,
  reports: TbChartBar,
  communication: TbMail,
  epf: TbShield,
  'inward & transfers': TbArrowsExchange,
  'inward and transfers': TbArrowsExchange,
  'statutory reports': TbScale,
  'shareholding query': TbUserSearch,
  'shareholding reports': TbReportAnalytics,
  'inward verification': TbShieldCheck,
  'inward data': TbFileImport,
}

const ICON_RULES: IconRule[] = [
  { test: (t) => /update inward|inward.?data.?update/.test(t), icon: TbCloudUpload },
  { test: (t) => /inward.?verif|inwarddataverification|request.?verif/.test(t), icon: TbShieldCheck },
  { test: (t) => /\binward.?data\b/.test(t), icon: TbFileImport },
  { test: (t) => /inward.*transfer|transfer.*inward|inward.?&|inward and transfer/.test(t), icon: TbArrowsExchange },
  { test: (t) => /shareholding.?report/.test(t), icon: TbReportAnalytics },
  { test: (t) => /shareholding.?quer/.test(t), icon: TbUserSearch },
  { test: (t) => /statutory.?report/.test(t), icon: TbScale },
  { test: (t) => /master.?updat/.test(t), icon: TbDatabase },
  { test: (t) => /corporate.?action/.test(t), icon: TbBolt },
  { test: (t) => /outstanding.?share/.test(t), icon: TbChartPie },
  { test: (t) => /reconcil/.test(t), icon: TbRefresh },
  { test: (t) => /communicat|bounced.?email|email.?list|\bmail\b|\bemail\b/.test(t), icon: TbMail },
  { test: (t) => /\biepf\b/.test(t), icon: TbShield },
  { test: (t) => /\bquer(?:y|ies)\b/.test(t), icon: TbSearch },
  { test: (t) => /welcome|dashboard|^home\b/.test(t), icon: TbHome },
  { test: (t) => /\breports\b|\breport\b/.test(t), icon: TbChartBar },
  { test: (t) => /\bcompany\b|companydetails|corporate(?!.?action)/.test(t), icon: TbBuilding },
]

function normalizeLabel(text: string): string {
  return text.trim().toLowerCase().replace(/\s+/g, ' ')
}

function menuMatchText(item: LoginMenu): string {
  const path = normalizeLegacyMenuPath(item.pageName).toLowerCase()
  const label = (item.displayName || '').toLowerCase()
  const displayMenu = (item.displayMenu || '').toLowerCase()
  return `${label} ${displayMenu} ${path}`.replace(/\s+/g, ' ').trim()
}

export function resolveMenuParentIcon(item: LoginMenu): IconType {
  const label = normalizeLabel(item.displayName || '')
  const displayMenu = normalizeLabel(item.displayMenu || '')

  if (label && LABEL_ICON_MAP[label]) {
    return LABEL_ICON_MAP[label]
  }

  if (displayMenu && LABEL_ICON_MAP[displayMenu]) {
    return LABEL_ICON_MAP[displayMenu]
  }

  const text = menuMatchText(item)

  for (const rule of ICON_RULES) {
    if (rule.test(text)) {
      return rule.icon
    }
  }

  return TbFolder
}

export function MenuParentIcon({ item }: { item: LoginMenu }) {
  const Icon = resolveMenuParentIcon(item)

  return (
    <span className="menu-parent__icon" aria-hidden>
      <Icon size={16} strokeWidth={2} />
    </span>
  )
}
